import { useEffect, useRef, useState, type RefObject } from 'react'
import { useLocation } from 'react-router'
import { useSessione } from '../servizi/sessione.tsx'
import { daSchermataHome, nellAppCidis, sistemaTelefono } from '../servizi/app-nativa.ts'
import { Riquadro } from './base.tsx'
import { IstruzioniElement } from './IstruzioniElement.tsx'
import { DisegnoQr } from './DisegnoQr.tsx'
import { indirizzoIstruzioni } from '../servizi/app-telefono.ts'

/**
 * «App per il telefono» in «Il mio profilo» (decisione di Davide dell'1/11/2026: niente store per ora).
 *
 * Android: l'app CIDIS si scarica da qui, firmata da Honey et Molly (/app/cidis.apk, con versione,
 * dimensione e impronte in /app/cidis-apk.json, scritti da ops/app-android.sh). iPhone: senza App
 * Store l'app nativa non si installa, quindi il gestionale si aggiunge alla schermata Home da Safari
 * (manifest.webmanifest).
 *
 * «Messenger», per chi lo usa: Element Classic sul telefono (IstruzioniElement.tsx). Dentro l'app restano
 * solo queste istruzioni, e per chi non usa il messenger il riquadro non compare.
 *
 * L'ancora `app-telefono` sta qui e non in Profilo.tsx: ci portano l'invito sopra la pagina e il suo codice
 * QR (AvvisoAppTelefono.tsx).
 */

type InfoApk = { versione: string; dimensione: number; sha256: string; certificato: string }

const MB = (byte: number) => `${(byte / 1048576).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`

const EVENTI_DI_CHI_LEGGE = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const

/**
 * /profilo#app-telefono: la pagina scorre fino al riquadro, una volta per arrivo. L'email di recupero,
 * sopra, si carica per conto suo e lo sposta in giu': per due secondi ogni cambio di altezza della pagina
 * lo riporta in cima, finche' chi legge non scorre o tocca lo schermo.
 */
function useArrivoAllAncora(ancora: RefObject<HTMLDivElement | null>) {
  const { hash, key } = useLocation()
  useEffect(() => {
    const elemento = ancora.current
    if (hash !== '#app-telefono' || !elemento) return
    const porta = () => elemento.scrollIntoView({ block: 'start' })
    requestAnimationFrame(porta)
    const osservatore = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(porta)
    osservatore?.observe(document.body)
    const smetti = () => {
      osservatore?.disconnect()
      window.clearTimeout(tempo)
      for (const e of EVENTI_DI_CHI_LEGGE) window.removeEventListener(e, smetti)
    }
    const tempo = window.setTimeout(smetti, 2000)
    for (const e of EVENTI_DI_CHI_LEGGE) window.addEventListener(e, smetti, { passive: true })
    return smetti
  }, [ancora, hash, key])
}

export function AppTelefono() {
  const { io, puo } = useSessione()
  const ancora = useRef<HTMLDivElement>(null)
  useArrivoAllAncora(ancora)
  const [apk, setApk] = useState<InfoApk | null>(null)
  const [mancaApk, setMancaApk] = useState(false)
  useEffect(() => {
    fetch('/app/cidis-apk.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() as Promise<InfoApk> : Promise.reject(new Error())))
      .then(setApk)
      .catch(() => setMancaApk(true))
  }, [])

  const indirizzoMessenger = io?.indirizzi?.messenger ?? null
  const messenger = puo('chat.usa') && indirizzoMessenger ? (
    <div className="app-telefono-messenger">
      <h3>Messenger</h3>
      <p>Sul telefono il messenger si usa con l’app Element Classic, non con Element X. Scegli il tuo telefono:</p>
      <IstruzioniElement indirizzo={indirizzoMessenger} />
    </div>
  ) : null

  const nellApp = nellAppCidis()
  if (nellApp && !messenger) return null
  const s = sistemaTelefono()

  const android = (
    <div className="app-telefono-sistema">
      <h3>Android</h3>
      {apk ? (
        <>
          <div className="app-telefono-scarica">
            <a className="bottone bottone-primario" href="/app/cidis.apk" download="CIDIS.apk">
              Scarica l’app per Android
            </a>
            <span className="piccolo tenue">versione {apk.versione}, {MB(apk.dimensione)}</span>
          </div>
          <ol className="app-telefono-passi">
            <li>Tocca «Scarica l’app per Android» e, finito lo scaricamento, apri il file CIDIS.apk.</li>
            <li>Se il telefono lo chiede, consenti al browser di installare app da questa fonte, poi «Installa».</li>
            <li>Apri CIDIS ed entra con il tuo nome utente e la tua password. Da lì l’app si sblocca con
              l’impronta, il volto o il codice del telefono.</li>
          </ol>
          <details className="app-telefono-verifica">
            <summary>L’app non viene da Google Play: come riconoscerla</summary>
            <p className="piccolo tenue">
              È firmata da Honey et Molly per CIDIS. Impronta del file (SHA-256):{' '}
              <code className="app-telefono-impronta">{apk.sha256}</code>
            </p>
          </details>
        </>
      ) : (
        <p className="piccolo tenue">{mancaApk ? 'L’app per Android è in preparazione.' : 'Un momento…'}</p>
      )}
    </div>
  )

  const iphone = (
    <div className="app-telefono-sistema">
      <h3>iPhone</h3>
      {s === 'iphone' && daSchermataHome() ? (
        <p>Stai già usando il gestionale dalla schermata Home.</p>
      ) : (
        <>
          <ol className="app-telefono-passi">
            <li>Apri il gestionale con Safari: {io?.indirizzi?.gestionale ?? window.location.origin}.</li>
            <li>Tocca il pulsante Condividi (il quadrato con la freccia verso l’alto).</li>
            <li>Scegli «Aggiungi alla schermata Home», poi «Aggiungi».</li>
          </ol>
          <p className="piccolo tenue">
            Comparirà l’icona CIDIS e il gestionale si aprirà a tutto schermo. La prima volta entri di nuovo con
            nome utente e password; poi «Attiva Face ID» lo tiene connesso per 30 giorni dall’ultimo uso, e si
            apre con Face ID. L’app per iPhone arriverà con l’App Store.
          </p>
        </>
      )}
    </div>
  )

  // Dal telefono prima il proprio sistema, l'altro chiuso sotto; dal computer i due affiancati, con il
  // codice QR che apre questa pagina sul telefono.
  const sistemi = s === 'android' || s === 'iphone' ? (
    <>
      {s === 'android' ? android : iphone}
      <details className="app-telefono-altro">
        <summary>{s === 'android' ? 'Hai un iPhone?' : 'Hai un telefono Android?'}</summary>
        {s === 'android' ? iphone : android}
      </details>
    </>
  ) : (
    <>
      <div className="app-telefono-dal-computer">
        {io && <DisegnoQr testo={indirizzoIstruzioni(io)}
                          etichetta="Codice QR che apre questa pagina sul telefono" />}
        <p>
          Inquadra il codice con la fotocamera del telefono: questa pagina si apre lì, con il pulsante per
          scaricare l’app o i passi per l’iPhone.
        </p>
      </div>
      <div className="app-telefono-sistemi">{android}{iphone}</div>
    </>
  )

  return (
    <div id="app-telefono" ref={ancora}>
      <Riquadro titolo="App per il telefono">
        {!nellApp && sistemi}
        {messenger}
      </Riquadro>
    </div>
  )
}
